// src/components/governance/BoardOfTrusteesPortal.tsx
// DROP 26B — Board of Trustees Full Administrative Portal
// TOP-LEVEL GOVERNANCE: Strategic oversight, budget approval, resolutions
// Authorizer Governance — view-focused + authorization actions

import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Crown, Users, FileText, DollarSign, BookOpen,
  CheckCircle, Plus,
  Phone, Mail, Loader2, AlertCircle,
  Award, Eye, ThumbsUp, ThumbsDown
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface Trustee {
  id: string; name: string; role: string; occupation?: string;
  organization?: string; state?: string; bio?: string;
  photoUrl?: string; sortOrder?: number; phone?: string;
  email?: string; title?: string;
}

interface Resolution {
  id: string; title: string; description: string;
  proposedBy: string; status: 'pending' | 'approved' | 'rejected' | 'deferred';
  date: string; votes?: { for: number; against: number; abstain: number };
  category: 'budget' | 'policy' | 'strategic' | 'operational';
}

interface BudgetItem {
  id: string; category: string; allocated: number; spent: number; currency: string;
}

interface Minute {
  id: string; title: string; date: string; attendees: string;
  agenda: string; decisions: string; nextMeeting?: string;
}

// ─── Design constants ─────────────────────────────────────────

const C = {
  bg:       '#070D0A', surface: '#0E1812', card: '#121E16',
  border:   '#1C2E22', gold:   '#C9A84C', goldLt: '#E2C97E',
  text:     '#EDE8DC', muted:  'rgba(237,232,220,0.5)',
  dim:      'rgba(237,232,220,0.25)', green:  '#0F5132',
  darkGreen:'#0A3D26',
};

const fmt = (n: number) => `₦${n.toLocaleString()}`;
const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-NG', { day:'numeric', month:'short', year:'numeric' });

// ─── Metric Card ─────────────────────────────────────────────

function MetricCard({ icon, label, value, sub, color = C.gold }:
  { icon: React.ReactNode; label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: '16px 20px',
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <span style={{ color }}>{icon}</span>
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 11, color: C.muted }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ─── Tab Button ───────────────────────────────────────────────

function TabBtn({ active, onClick, children, icon }:
  { key?: React.Key; active: boolean; onClick: () => void; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
        background: active ? C.green : 'transparent',
        color: active ? C.goldLt : C.muted,
        fontSize: 13, fontWeight: active ? 600 : 400,
        transition: 'all 0.15s', whiteSpace: 'nowrap' as const,
        borderBottom: active ? `2px solid ${C.gold}` : '2px solid transparent',
      }}
    >
      {icon && <span style={{ opacity: 0.8 }}>{icon}</span>}
      {children}
    </button>
  );
}

// ─── Status Badge ─────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    approved:    { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: '✓ Approved' },
    rejected:    { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', label: '✗ Rejected' },
    pending:     { bg: 'rgba(201,168,76,0.12)', color: C.gold,   label: '⏳ Pending' },
    deferred:    { bg: 'rgba(99,102,241,0.12)', color: '#818cf8', label: '⟳ Deferred' },
    active:      { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: '● Active' },
    established: { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: '✓ Established' },
    pending_loc: { bg: 'rgba(201,168,76,0.12)', color: C.gold,   label: '⏳ Pending' },
    dissolved:   { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', label: '✗ Dissolved' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600,
      background: s.bg, color: s.color, letterSpacing: 0.5,
    }}>{s.label}</span>
  );
}

// ─── Resolution Category Badge ────────────────────────────────

function CatBadge({ cat }: { cat: string }) {
  const map: Record<string, string> = {
    budget: '#C9A84C', policy: '#818cf8', strategic: '#22c55e', operational: '#38bdf8',
  };
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 12, fontSize: 9, fontWeight: 700,
      background: `${map[cat] || C.gold}20`, color: map[cat] || C.gold,
      textTransform: 'uppercase' as const, letterSpacing: 1,
    }}>{cat}</span>
  );
}

// ─── Main Component ───────────────────────────────────────────

interface Props { currentUser?: { email: string; systemRole?: string } | null; }

export default function BoardOfTrusteesPortal({ currentUser }: Props) {
  const [tab, setTab] = useState<'overview' | 'members' | 'resolutions' | 'budget' | 'minutes'>('overview');
  const [trustees, setTrustees] = useState<Trustee[]>([]);
  const [budgets, setBudgets] = useState<BudgetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Local resolution + minutes state (stored locally until server routes added)
  const [resolutions, setResolutions] = useState<Resolution[]>([
    {
      id: 'r1', title: 'Approval of ERUE Festival 2026 Budget',
      description: 'Review and formal approval of the comprehensive budget for the ERUE Festival of Uzairue Kingdom, December 24–30, 2026.',
      proposedBy: 'Chairman, Board of Trustees', status: 'approved',
      date: new Date().toISOString(), category: 'budget',
      votes: { for: 7, against: 0, abstain: 1 },
    },
    {
      id: 'r2', title: 'Heritage Fund Endowment Policy',
      description: 'Establish a permanent Heritage Endowment Fund to receive festival surplus and diaspora contributions for community development.',
      proposedBy: 'Financial Adviser', status: 'pending',
      date: new Date().toISOString(), category: 'policy',
      votes: { for: 4, against: 1, abstain: 2 },
    },
    {
      id: 'r3', title: 'Strategic Partnership with Etsako West LGA',
      description: 'Authorize the CWC to formally engage Etsako West Local Government for co-sponsorship and logistical support of the Grand Finale.',
      proposedBy: 'Vice-Chairman', status: 'approved',
      date: new Date().toISOString(), category: 'strategic',
      votes: { for: 8, against: 0, abstain: 0 },
    },
  ]);

  const [minutes, setMinutes] = useState<Minute[]>([
    {
      id: 'm1', title: '1st Board of Trustees Meeting — 2026 Festival Planning',
      date: '2026-09-01', attendees: '8 of 9 trustees present',
      agenda: 'Festival budget approval, CWC oversight, heritage fund discussion, Paystack integration review.',
      decisions: 'Budget approved. CWC empowered to proceed. Heritage fund motion deferred for legal review.',
      nextMeeting: '2026-11-01',
    },
  ]);

  // Add resolution form
  const [showResForm, setShowResForm] = useState(false);
  // DROP 34 — Add trustee form
  const [showTrusteeForm, setShowTrusteeForm] = useState(false);
  const [trusteeForm, setTrusteeForm] = useState({ name:'', role:'Trustee', occupation:'', organization:'', state:'Nigeria', bio:'', phone:'', email:'' });
  const [savingTrustee, setSavingTrustee] = useState(false);

  const handleAddTrustee = async () => {
    if (!(trusteeForm as any).name || !(trusteeForm as any).name.trim()) {
      alert('Full name is required');
      return;
    }
    setSavingTrustee(true);
    try {
      const token = localStorage.getItem('erue_auth_token')
                 || sessionStorage.getItem('erue_auth_token') || '';
      const res = await fetch('/api/admin/board-of-trustees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(trusteeForm),
      });
      if (!res.ok) throw new Error('Save failed');
      setShowTrusteeForm(false);
      loadData();
    } catch (err) {
      alert('Failed to save trustee. Please try again.');
    } finally {
      setSavingTrustee(false);
    }
  };
  const [resForm, setResForm] = useState({ title: '', description: '', category: 'strategic', proposedBy: '' });

  // Add minute form
  const [showMinForm, setShowMinForm] = useState(false);
  const [minForm, setMinForm] = useState({ title: '', date: '', attendees: '', agenda: '', decisions: '', nextMeeting: '' });

  const flash = (type: 'ok' | 'err', text: string) => {
    setMsg({ type, text }); setTimeout(() => setMsg(null), 4000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, bRes] = await Promise.all([
        fetch('/api/public/board-of-trustees'),
        fetch('/api/accounting/budgets', { headers: { Authorization: `Bearer ${localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || ''}` } }),
      ]);
      const tData = await tRes.json();
      setTrustees(tData.members || []);
      if (bRes.ok) {
        const bData = await bRes.json();
        setBudgets(bData.budgets || []);
      }
    } catch { flash('err', 'Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleVote = (resId: string, vote: 'for' | 'against' | 'abstain') => {
    setResolutions(prev => prev.map(r => {
      if (r.id !== resId) return r;
      const v = r.votes || { for: 0, against: 0, abstain: 0 };
      return { ...r, votes: { ...v, [vote]: v[vote] + 1 } };
    }));
    flash('ok', `Vote recorded: ${vote.toUpperCase()}`);
  };

  const handleApprove = (resId: string) => {
    setResolutions(prev => prev.map(r =>
      r.id === resId ? { ...r, status: 'approved' as const } : r
    ));
    flash('ok', 'Resolution approved and recorded.');
  };

  const handleReject = (resId: string) => {
    setResolutions(prev => prev.map(r =>
      r.id === resId ? { ...r, status: 'rejected' as const } : r
    ));
    flash('ok', 'Resolution rejected and recorded.');
  };

  const handleAddResolution = () => {
    if (!resForm.title.trim()) { flash('err', 'Title is required'); return; }
    const newRes: Resolution = {
      id: `r${Date.now()}`, ...resForm as any,
      status: 'pending', date: new Date().toISOString(),
      votes: { for: 0, against: 0, abstain: 0 },
    };
    setResolutions(prev => [newRes, ...prev]);
    setResForm({ title: '', description: '', category: 'strategic', proposedBy: '' });
    setShowResForm(false);
    flash('ok', 'Resolution added for board consideration.');
  };

  const handleAddMinute = () => {
    if (!minForm.title.trim()) { flash('err', 'Title is required'); return; }
    setMinutes(prev => [{ id: `m${Date.now()}`, ...minForm }, ...prev]);
    setMinForm({ title: '', date: '', attendees: '', agenda: '', decisions: '', nextMeeting: '' });
    setShowMinForm(false);
    flash('ok', 'Meeting minutes recorded.');
  };

  const userRole = (currentUser?.systemRole || '').toUpperCase();
  const isAdmin = ['PLATFORM_ADMIN', 'FESTIVAL_COORDINATOR'].includes(userRole) ||
                  currentUser?.email === 'erueuzairue@gmail.com';

  const chairman = trustees.find(t => t.role === 'Board Chairman');
  const otherTrustees = trustees.filter(t => t.role !== 'Board Chairman');
  const totalBudget = budgets.reduce((s, b) => s + b.allocated, 0);
  const totalSpent = budgets.reduce((s, b) => s + b.spent, 0);
  const pendingRes = resolutions.filter(r => r.status === 'pending').length;
  const approvedRes = resolutions.filter(r => r.status === 'approved').length;

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '24px 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px' }}>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${C.darkGreen} 0%, ${C.green} 100%)`,
          borderRadius: 16, padding: '28px 32px', marginBottom: 24,
          border: `1px solid rgba(201,168,76,0.3)`,
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120,
            borderRadius: '50%', background: 'rgba(201,168,76,0.08)', border: `1px solid rgba(201,168,76,0.1)` }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(201,168,76,0.15)',
                  border: `1px solid rgba(201,168,76,0.3)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Shield size={18} style={{ color: C.gold }} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' as const, color: C.gold }}>
                  Top-Level Governance · Authorizer Body
                </span>
              </div>
              <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: 26, fontWeight: 700, color: C.text, margin: 0, lineHeight: 1.2 }}>
                Board of Trustees
              </h1>
              <p style={{ fontSize: 13, color: C.muted, margin: '6px 0 0' }}>
                Strategic oversight · Budget approval · Policy authorization · Legacy stewardship
              </p>
            </div>
            <div style={{ fontSize: 11, color: C.dim, textAlign: 'right' as const }}>
              <div style={{ color: C.gold, fontWeight: 600, marginBottom: 4 }}>ERUE Festival of Uzairue Kingdom</div>
              <div>December 24–30, 2026</div>
              <div>Uzairue Kingdom, Etsako West LGA</div>
            </div>
          </div>
        </div>

        {/* Flash message */}
        {msg && (
          <div style={{
            padding: '10px 16px', borderRadius: 8, marginBottom: 16,
            background: msg.type === 'ok' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${msg.type === 'ok' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: msg.type === 'ok' ? '#22c55e' : '#ef4444', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {msg.type === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
            {msg.text}
          </div>
        )}

        {/* Metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
          <MetricCard icon={<Users size={18} />} label="Board Members" value={trustees.length} sub="Active trustees" />
          <MetricCard icon={<FileText size={18} />} label="Resolutions" value={resolutions.length} sub={`${pendingRes} pending · ${approvedRes} approved`} color="#818cf8" />
          <MetricCard icon={<DollarSign size={18} />} label="Total Budget" value={fmt(totalBudget)} sub={`${fmt(totalSpent)} disbursed`} />
          <MetricCard icon={<BookOpen size={18} />} label="Meeting Records" value={minutes.length} sub="Minutes on file" color="#38bdf8" />
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}`,
          marginBottom: 24, overflowX: 'auto' as const, paddingBottom: 0,
        }}>
          {([
            { key: 'overview',     label: 'Overview',     icon: <Eye size={13} /> },
            { key: 'members',      label: 'Trustees',     icon: <Users size={13} /> },
            { key: 'resolutions',  label: 'Resolutions',  icon: <FileText size={13} /> },
            { key: 'budget',       label: 'Budget Oversight', icon: <DollarSign size={13} /> },
            { key: 'minutes',      label: 'Minutes',      icon: <BookOpen size={13} /> },
          ] as const).map(t => (
            <TabBtn key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} icon={t.icon}>
              {t.label}
            </TabBtn>
          ))}
        </div>

        {/* Tab Content */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Loader2 size={32} style={{ color: C.gold, animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <>
            {/* ── OVERVIEW ── */}
            {/* ── Add Trustee Floating Form ── */}
          {isAdmin && showTrusteeForm && (
            <div style={{ background:'#121E16', border:'1px solid #1C2E22', borderRadius:12,
              padding:'20px 24px', marginBottom:20 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#C9A84C', marginBottom:16,
                letterSpacing:1, textTransform:'uppercase' as const }}>Add Board Trustee</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                {[
                  { label:'Full Name *', key:'name', placeholder:'Enter full name' },
                  { label:'Role *', key:'role', placeholder:'Trustee' },
                  { label:'Occupation', key:'occupation', placeholder:'e.g. Engineer, Lawyer' },
                  { label:'Organization', key:'organization', placeholder:'Company/Institution' },
                  { label:'State', key:'state', placeholder:'e.g. Edo, Lagos' },
                  { label:'Phone', key:'phone', placeholder:'+234...' },
                  { label:'Email', key:'email', placeholder:'email@domain.com' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ display:'block', fontSize:10, color:'rgba(237,232,220,0.5)',
                      textTransform:'uppercase' as const, letterSpacing:1, marginBottom:4 }}>{f.label}</label>
                    <input value={(trusteeForm as any)[f.key]}
                      onChange={e => setTrusteeForm(p => ({ ...p, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      style={{ width:'100%', background:'#070D0A', border:'1px solid #1C2E22',
                        borderRadius:6, padding:'8px 12px', color:'#EDE8DC', fontSize:13,
                        outline:'none', boxSizing:'border-box' as const }} />
                  </div>
                ))}
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={{ display:'block', fontSize:10, color:'rgba(237,232,220,0.5)',
                    textTransform:'uppercase' as const, letterSpacing:1, marginBottom:4 }}>Bio</label>
                  <textarea value={trusteeForm.bio}
                    onChange={e => setTrusteeForm(p => ({ ...p, bio: e.target.value }))}
                    placeholder="Brief biography..."
                    rows={3}
                    style={{ width:'100%', background:'#070D0A', border:'1px solid #1C2E22',
                      borderRadius:6, padding:'8px 12px', color:'#EDE8DC', fontSize:13,
                      outline:'none', boxSizing:'border-box' as const, resize:'vertical' as const }} />
                </div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <button onClick={handleAddTrustee} disabled={savingTrustee}
                  style={{ padding:'9px 20px', background:'#0F5132',
                    border:'1px solid rgba(201,168,76,0.4)', borderRadius:8,
                    color:'#C9A84C', fontSize:12, fontWeight:700,
                    cursor:savingTrustee?'wait':'pointer', opacity:savingTrustee?0.6:1 }}>
                  {savingTrustee ? 'Saving...' : 'Add Trustee'}
                </button>
                <button onClick={() => setShowTrusteeForm(false)}
                  style={{ padding:'9px 20px', background:'transparent',
                    border:'1px solid #1C2E22', borderRadius:8,
                    color:'rgba(237,232,220,0.5)', fontSize:12, cursor:'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {tab === 'overview' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Chairman card */}
                {chairman && (
                  <div style={{ gridColumn: '1 / -1', background: C.card, border: `2px solid rgba(201,168,76,0.3)`,
                    borderRadius: 14, padding: 24, display: 'flex', alignItems: 'center', gap: 20 }}>
                    <div style={{ width: 72, height: 72, borderRadius: '50%',
                      background: `${C.gold}15`, border: `2px solid rgba(201,168,76,0.4)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Crown size={30} style={{ color: C.gold }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: C.gold, letterSpacing: 2, textTransform: 'uppercase' as const, marginBottom: 4 }}>
                        Board Chairman
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: C.text, fontFamily: 'Cinzel, serif' }}>
                        {chairman.name}
                      </div>
                      {chairman.organization && <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{chairman.organization}</div>}
                      {chairman.state && <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>📍 {chairman.state}</div>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                      {chairman.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.muted }}>
                          <Phone size={12} style={{ color: C.gold }} /> {chairman.phone}
                        </div>
                      )}
                      {chairman.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.muted }}>
                          <Mail size={12} style={{ color: C.gold }} /> {chairman.email}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mandate */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                  <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 14, color: C.gold, marginBottom: 12 }}>
                    🏛️ Governance Mandate
                  </h3>
                  {[
                    { icon: '📋', title: 'Policy Setting', desc: 'Establish and ratify broad festival policies and governance frameworks' },
                    { icon: '💰', title: 'Budget Approval', desc: 'Review and authorize all major financial allocations and expenditures' },
                    { icon: '🤝', title: 'External Relations', desc: 'Connect the Kingdom with government, corporate, and diaspora partners' },
                    { icon: '📜', title: 'Legacy Preservation', desc: 'Safeguard the long-term heritage and institutional memory of ERUE Festival' },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12, padding: '8px 12px',
                      background: `${C.bg}80`, borderRadius: 8, border: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 16 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{item.title}</div>
                        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pending actions */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                  <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 14, color: C.gold, marginBottom: 12 }}>
                    ⏳ Pending Actions
                  </h3>
                  {resolutions.filter(r => r.status === 'pending').length === 0 ? (
                    <div style={{ textAlign: 'center' as const, color: C.dim, fontSize: 13, padding: 20 }}>
                      No pending resolutions
                    </div>
                  ) : (
                    resolutions.filter(r => r.status === 'pending').map(r => (
                      <div key={r.id} style={{ background: `${C.bg}80`, borderRadius: 8, padding: '10px 14px',
                        border: `1px solid rgba(201,168,76,0.2)`, marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{r.title}</span>
                          <CatBadge cat={r.category} />
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <button onClick={() => handleApprove(r.id)}
                            style={{ padding: '4px 10px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                              borderRadius: 6, color: '#22c55e', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 4 }}>
                            <ThumbsUp size={11} /> Approve
                          </button>
                          <button onClick={() => handleReject(r.id)}
                            style={{ padding: '4px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                              borderRadius: 6, color: '#ef4444', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 4 }}>
                            <ThumbsDown size={11} /> Reject
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* ── MEMBERS ── */}
            {tab === 'members' && (
              <div>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, margin: 0 }}>
                    Board Members ({trustees.length})
                  </h2>
                </div>
                {trustees.length === 0 ? (
                  <div style={{ textAlign: 'center' as const, padding: 60, color: C.dim }}>
                    <Shield size={40} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                    <p style={{ color:'rgba(237,232,220,0.5)', fontSize:13, marginBottom:12 }}>
                      No trustees recorded yet.
                    </p>
                    {isAdmin && (
                      <button onClick={() => setShowTrusteeForm(true)}
                        style={{ padding:'8px 20px', background:'#0F5132',
                          border:'1px solid rgba(201,168,76,0.4)', borderRadius:8,
                          color:'#C9A84C', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        + Add First Trustee
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                    {trustees.map(t => (
                      <div key={t.id} style={{ background: C.card, border: `1px solid ${t.role === 'Board Chairman' ? 'rgba(201,168,76,0.4)' : C.border}`,
                        borderRadius: 12, padding: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                          <div style={{ width: 44, height: 44, borderRadius: '50%',
                            background: t.role === 'Board Chairman' ? `${C.gold}20` : `${C.green}40`,
                            border: `1px solid ${t.role === 'Board Chairman' ? 'rgba(201,168,76,0.4)' : C.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {t.role === 'Board Chairman' ? <Crown size={18} style={{ color: C.gold }} /> :
                              <Award size={18} style={{ color: C.muted }} />}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: C.text,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{t.name}</div>
                            <div style={{ fontSize: 11, color: C.gold }}>{t.role}</div>
                          </div>
                        </div>
                        {t.occupation && <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>💼 {t.occupation}</div>}
                        {t.organization && <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>🏢 {t.organization}</div>}
                        {t.state && <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>📍 {t.state}</div>}
                        {t.bio && <p style={{ fontSize: 11, color: C.dim, lineHeight: 1.5, borderTop: `1px solid ${C.border}`, paddingTop: 10, margin: '10px 0 0' }}>{t.bio}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── RESOLUTIONS ── */}
            {tab === 'resolutions' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, margin: 0 }}>
                    Board Resolutions ({resolutions.length})
                  </h2>
                  <button onClick={() => setShowResForm(!showResForm)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                      background: C.green, border: `1px solid rgba(201,168,76,0.3)`, borderRadius: 8,
                      color: C.gold, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <Plus size={14} /> New Resolution
                  </button>
                </div>

                {showResForm && (
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                    <h3 style={{ color: C.gold, fontSize: 13, marginBottom: 14 }}>Propose New Resolution</h3>
                    {[
                      { label: 'Resolution Title *', key: 'title', type: 'text', placeholder: 'e.g. Approval of Heritage Fund Policy' },
                      { label: 'Proposed By', key: 'proposedBy', type: 'text', placeholder: 'e.g. Financial Adviser' },
                    ].map(field => (
                      <div key={field.key} style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>{field.label}</label>
                        <input type={field.type} placeholder={field.placeholder}
                          value={(resForm as any)[field.key]}
                          onChange={e => setResForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                            fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
                      </div>
                    ))}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                        textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Description</label>
                      <textarea rows={3} placeholder="Describe the resolution in detail..."
                        value={resForm.description}
                        onChange={e => setResForm(prev => ({ ...prev, description: e.target.value }))}
                        style={{ width: '100%', padding: '9px 12px', background: C.bg,
                          border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                          fontSize: 13, outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                        textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Category</label>
                      <select value={resForm.category}
                        onChange={e => setResForm(prev => ({ ...prev, category: e.target.value as any }))}
                        style={{ width: '100%', padding: '9px 12px', background: C.bg,
                          border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none' }}>
                        {['budget', 'policy', 'strategic', 'operational'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleAddResolution}
                        style={{ padding: '8px 18px', background: C.gold, border: 'none', borderRadius: 8,
                          color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Submit Resolution
                      </button>
                      <button onClick={() => setShowResForm(false)}
                        style={{ padding: '8px 14px', background: 'none', border: `1px solid ${C.border}`,
                          borderRadius: 8, color: C.muted, fontSize: 12, cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
                  {resolutions.map(r => (
                    <div key={r.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <CatBadge cat={r.category} />
                            <StatusBadge status={r.status} />
                          </div>
                          <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, margin: '6px 0 4px' }}>{r.title}</h3>
                          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{r.description}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        paddingTop: 10, borderTop: `1px solid ${C.border}`, flexWrap: 'wrap' as const, gap: 8 }}>
                        <div style={{ fontSize: 11, color: C.dim }}>
                          Proposed by: <span style={{ color: C.muted }}>{r.proposedBy}</span> · {fmtDate(r.date)}
                        </div>
                        {r.votes && (
                          <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
                            <span style={{ color: '#22c55e' }}>👍 {r.votes.for}</span>
                            <span style={{ color: '#ef4444' }}>👎 {r.votes.against}</span>
                            <span style={{ color: C.muted }}>⊖ {r.votes.abstain}</span>
                          </div>
                        )}
                        {r.status === 'pending' && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            {['for', 'against', 'abstain'].map(v => (
                              <button key={v} onClick={() => handleVote(r.id, v as any)}
                                style={{ padding: '4px 10px', background: 'none', border: `1px solid ${C.border}`,
                                  borderRadius: 6, color: C.muted, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                                  textTransform: 'capitalize' as const }}>
                                {v === 'for' ? '👍' : v === 'against' ? '👎' : '⊖'} {v}
                              </button>
                            ))}
                            <button onClick={() => handleApprove(r.id)}
                              style={{ padding: '4px 10px', background: 'rgba(34,197,94,0.1)',
                                border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6,
                                color: '#22c55e', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                              ✓ Approve
                            </button>
                            <button onClick={() => handleReject(r.id)}
                              style={{ padding: '4px 10px', background: 'rgba(239,68,68,0.1)',
                                border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6,
                                color: '#ef4444', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                              ✗ Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── BUDGET ── */}
            {tab === 'budget' && (
              <div>
                {/* CWC Workflow Banner */}
                <div style={{
                  background: 'rgba(56,189,248,0.07)', border: '1px solid rgba(56,189,248,0.2)',
                  borderRadius: 10, padding: '10px 16px', marginBottom: 20,
                  display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'rgba(237,232,220,0.6)',
                }}>
                  <span style={{ fontSize: 16 }}>🏛️</span>
                  <span>
                    <strong style={{ color: '#38bdf8' }}>Board Role — Oversight Only:</strong> Budget planning
                    and financial operations are managed by the CWC. The Board reviews and authorizes
                    budgets submitted by the Central Working Committee.
                  </span>
                </div>
                <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, marginBottom: 16 }}>
                  Budget Oversight & Authorization
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 24 }}>
                  <MetricCard icon={<DollarSign size={18} />} label="Total Allocated" value={fmt(totalBudget)} />
                  <MetricCard icon={<CheckCircle size={18} />} label="Total Disbursed" value={fmt(totalSpent)} color="#22c55e" />
                  <MetricCard icon={<Shield size={18} />} label="Remaining Balance" value={fmt(totalBudget - totalSpent)} color="#38bdf8" />
                </div>
                {budgets.length === 0 ? (
                  <div style={{ textAlign: 'center' as const, padding: 40, color: C.dim }}>
                    <DollarSign size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                    <p>Budget data will appear once the Accounting module is configured.</p>
                  </div>
                ) : (
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}`,
                      display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
                      fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: C.muted }}>
                      <span>Category</span><span>Allocated</span><span>Spent</span><span>Balance</span>
                    </div>
                    {budgets.map((b, i) => {
                      const pct = b.allocated > 0 ? Math.min(100, (b.spent / b.allocated) * 100) : 0;
                      return (
                        <div key={b.id} style={{ padding: '14px 20px',
                          borderBottom: i < budgets.length - 1 ? `1px solid ${C.border}` : 'none',
                          display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', alignItems: 'center', gap: 8 }}>
                          <div>
                            <div style={{ fontSize: 13, color: C.text, marginBottom: 4 }}>{b.category}</div>
                            <div style={{ height: 4, background: `${C.border}`, borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2,
                                background: pct > 90 ? '#ef4444' : pct > 70 ? C.gold : '#22c55e', transition: 'width 0.3s' }} />
                            </div>
                          </div>
                          <span style={{ fontSize: 13, color: C.text }}>{fmt(b.allocated)}</span>
                          <span style={{ fontSize: 13, color: pct > 80 ? '#ef4444' : C.muted }}>{fmt(b.spent)}</span>
                          <span style={{ fontSize: 13, color: '#22c55e' }}>{fmt(b.allocated - b.spent)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── MINUTES ── */}
            {tab === 'minutes' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, margin: 0 }}>
                    Meeting Minutes ({minutes.length})
                  </h2>
                  <button onClick={() => setShowMinForm(!showMinForm)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                      background: C.green, border: `1px solid rgba(201,168,76,0.3)`, borderRadius: 8,
                      color: C.gold, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <Plus size={14} /> Record Minutes
                  </button>
                </div>

                {showMinForm && (
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                    <h3 style={{ color: C.gold, fontSize: 13, marginBottom: 14 }}>Record Meeting Minutes</h3>
                    {[
                      { label: 'Meeting Title *', key: 'title', placeholder: 'e.g. 2nd Board Meeting — November 2026' },
                      { label: 'Date', key: 'date', placeholder: 'YYYY-MM-DD' },
                      { label: 'Attendees', key: 'attendees', placeholder: 'e.g. 7 of 9 trustees present' },
                      { label: 'Agenda', key: 'agenda', placeholder: 'Items discussed...' },
                      { label: 'Decisions Made', key: 'decisions', placeholder: 'Key decisions and resolutions...' },
                      { label: 'Next Meeting', key: 'nextMeeting', placeholder: 'YYYY-MM-DD' },
                    ].map(field => (
                      <div key={field.key} style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>{field.label}</label>
                        <input type="text" placeholder={field.placeholder}
                          value={(minForm as any)[field.key]}
                          onChange={e => setMinForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                            fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleAddMinute}
                        style={{ padding: '8px 18px', background: C.gold, border: 'none', borderRadius: 8,
                          color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Save Minutes
                      </button>
                      <button onClick={() => setShowMinForm(false)}
                        style={{ padding: '8px 14px', background: 'none', border: `1px solid ${C.border}`,
                          borderRadius: 8, color: C.muted, fontSize: 12, cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
                  {minutes.map(m => (
                    <div key={m.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div>
                          <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>{m.title}</h3>
                          <div style={{ fontSize: 11, color: C.dim }}>
                            📅 {m.date} · 👥 {m.attendees}
                          </div>
                        </div>
                      </div>
                      {m.agenda && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: C.muted, marginBottom: 4 }}>Agenda</div>
                          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, margin: 0 }}>{m.agenda}</p>
                        </div>
                      )}
                      {m.decisions && (
                        <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 8, padding: '10px 14px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#22c55e', marginBottom: 4 }}>Decisions</div>
                          <p style={{ fontSize: 12, color: C.text, lineHeight: 1.5, margin: 0 }}>{m.decisions}</p>
                        </div>
                      )}
                      {m.nextMeeting && (
                        <div style={{ marginTop: 10, fontSize: 11, color: C.gold }}>
                          📅 Next Meeting: {m.nextMeeting}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
